import React, { useState } from 'react';
import styled from 'styled-components';
import { defer } from '/imports/utils';
import { InputProps, InputStyled } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';
import { DateTime } from 'luxon';
import { useUser } from '/imports/hooks';

const DateTimeInputStyled = styled(InputStyled)`
  /* Firefox */
  input[type=number] {
    appearance: inherit;
    -moz-appearance: textfield;
  }
`;

export interface DateTimeInputProps extends InputProps<Date> {
  className?: string;
  disabled?: boolean;
  step?: string;
  min?: string;
  max?: string;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  seconds?: boolean;
  zone?: string;
}

/** Localized datetime-local input that maps between a JS Date and the user's timezone. */
export const DateTimeInput = (props: DateTimeInputProps) => {
  const user = useUser();
  const format = props.seconds ? 'yyyy-MM-dd\'T\'HH:mm:ss' : 'yyyy-MM-dd\'T\'HH:mm';
  const zone = props.zone || user?.timezone;
  const [strValue, setStrValue] = useState(props.value !== undefined ? `${DateTime.fromJSDate(props.value, { zone }).toFormat(format)}` : '');
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  /** Clears the value and fires onChange(undefined) then onClear. */
  const clearInput = () => {
    props.onChange?.(undefined);
    defer(() => props.onClear?.());
  };
  /**
   * Parses the input string in the active timezone and propagates the Date.
   * @param e - change event from the native input
   */
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = DateTime.fromFormat(e.target.value, format, { zone }).toJSDate();
    setStrValue(`${e.target.value}`);
    props.onChange?.(value, e);
  };
  /**
   * Forwards the blur event to the onBlur prop.
   * @param e - focus/blur event from the native input
   */
  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => props.onBlur?.(e);
  return (
    <DateTimeInputStyled id={props.id} className={`input number-input ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {hasStartIcon && <Icon className="icon-start" icon={props.icon as any} />}
      <input
        id={props.inputId}
        className={hasStartIcon ? 'has-start-icon' : ''}
        type="datetime-local"
        step={props.step || ''}
        min={props.min}
        max={props.max}
        placeholder={props.placeholder || ''}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        value={strValue}
        onChange={(e) => onChange(e)}
        onClick={(e) => props.onClick?.(e)}
        onFocus={props.onFocus}
        onBlur={(e) => onBlur(e)}
        onKeyUp={(e) => {
          props.onKeyPress?.(e);
          if (e.key === 'Enter') defer(() => props.onEnter?.(props.value, e));
        }}
        autoComplete={props.autoComplete}
      />
      {
        props.value !== undefined &&
        !props.disabled &&
        !(props.type === 'color') &&
        <Icon className="clear" icon="cancel" onClick={() => clearInput()} /> || null
      }
    </DateTimeInputStyled>
  );
};